import * as THREE from 'three';
import { DisplayUnit, formatDistanceLabel, quantizeMetersToGrid } from './distanceUnits';

/** Meters display unit — default when no unit is supplied to a resolve. */
const METER_UNIT: DisplayUnit = { code: 'm', unitspermeter: 1.0 };

/**
 * Reference segment for the snap to test against. World-space XY; Z ignored.
 */
export interface ConstraintReferenceSegment {
  start: THREE.Vector2;
  end: THREE.Vector2;
}

export type ConstraintSnapReference = ConstraintReferenceSegment;
export type ConstraintSnapKind = 'fixed-distance';

export interface ConstraintSnapResult {
  /** Snapped vertex position in world XY. */
  adjusted: THREE.Vector2;
  kind: ConstraintSnapKind;
  reference: ConstraintReferenceSegment;
  /** Point on the reference where the snap ray hit. */
  foot: THREE.Vector2;
  /** Perpendicular distance from `adjusted` to `foot` (m). */
  distance: number;
  /** Short label for the indicator ("5 cm", "1.20 m"). */
  label: string;
}

export interface ConstraintSnapConfig {
  /** Max ray distance. Beyond this no snap. */
  maxRange: number;
}

const DEFAULT_CONFIG: ConstraintSnapConfig = {
  maxRange: 2.0,
};

export interface ResolveContext {
  /** Direction the snap rays travel along (both `+dragAxis` and `-dragAxis`). */
  dragAxis: THREE.Vector2;
  /**
   * Active display unit for the snap label. Geometry stays metric; this only
   * controls how the snapped distance is rendered. Defaults to meters.
   */
  unit?: DisplayUnit;
}

/**
 * Minimal snap engine: shoot two rays from the candidate perpendicular to
 * the prompt line (along `dragAxis` and its negation). For each ray, take
 * the first reference segment it hits. Of those two hits, the closer one
 * wins; its distance is quantized to the active display-unit grid.
 *
 * That's it. No occluders, no half-plane filtering, no per-segment metadata.
 */
export class LineConstraintSnap {
  constructor(private config: ConstraintSnapConfig = DEFAULT_CONFIG) {}

  setConfig(config: Partial<ConstraintSnapConfig>): void {
    this.config = { ...this.config, ...config };
  }

  resolve(candidateWorld: THREE.Vector3, references: ConstraintSnapReference[], ctx: ResolveContext): ConstraintSnapResult | null {
    if (references.length === 0) return null;
    const candidate = new THREE.Vector2(candidateWorld.x, candidateWorld.y);
    const axis = ctx.dragAxis.clone();
    if (!isFinite(axis.x) || !isFinite(axis.y) || axis.lengthSq() < 1e-12) return null;
    axis.normalize();

    const hitPlus = this.castRay(candidate, axis, references);
    const hitMinus = this.castRay(candidate, axis.clone().negate(), references);

    let best: RayHit | null = null;
    if (hitPlus && hitMinus) {
      best = hitPlus.distance <= hitMinus.distance ? hitPlus : hitMinus;
    } else {
      best = hitPlus ?? hitMinus;
    }
    if (!best) return null;

    // Quantize on the display-unit grid so the snapped value is a round number
    // in the unit the user sees (the result is still metric for the geometry).
    const snappedAbs = quantizeMetersToGrid(best.distance, ctx.unit ?? METER_UNIT);
    // Snapped vertex sits along the ray's reverse direction at the quantized distance.
    const adjusted = best.foot.clone().add(best.rayDir.clone().multiplyScalar(-snappedAbs));

    return {
      adjusted,
      kind: 'fixed-distance',
      reference: best.reference,
      foot: best.foot,
      distance: snappedAbs,
      label: formatDistanceLabel(snappedAbs, ctx.unit ?? METER_UNIT),
    };
  }

  /**
   * Cast a finite ray from `origin` along `rayDir`. Return the closest
   * segment intersection within `maxRange`. Each segment is treated as
   * its actual finite extent (no infinite-line tricks).
   */
  private castRay(origin: THREE.Vector2, rayDir: THREE.Vector2, references: ConstraintSnapReference[]): RayHit | null {
    let best: RayHit | null = null;
    for (const ref of references) {
      const dx = ref.end.x - ref.start.x;
      const dy = ref.end.y - ref.start.y;
      const segLen = Math.hypot(dx, dy);
      if (segLen < 1e-9) continue;

      // Solve origin + t * rayDir = ref.start + u * segDir, with u in [0, segLen].
      // det = cross(rayDir, segDir/segLen). Cancel the segLen by using raw segDir below.
      const det = rayDir.x * dy - rayDir.y * dx;
      if (Math.abs(det) < 1e-9) continue;
      const ox = ref.start.x - origin.x;
      const oy = ref.start.y - origin.y;
      const t = (ox * dy - oy * dx) / det;
      const uRaw = (ox * rayDir.y - oy * rayDir.x) / det;
      if (t < 0 || t > this.config.maxRange) continue;
      if (uRaw < 0 || uRaw > 1) continue;

      if (!best || t < best.distance) {
        const foot = new THREE.Vector2(origin.x + rayDir.x * t, origin.y + rayDir.y * t);
        best = { reference: ref, foot, distance: t, rayDir: rayDir.clone() };
      }
    }
    return best;
  }
}

interface RayHit {
  reference: ConstraintSnapReference;
  foot: THREE.Vector2;
  distance: number;
  rayDir: THREE.Vector2;
}
